import { Button } from "@babackapp/ui/components/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@babackapp/ui/components/table";
import { format } from "date-fns";
import { CloudDownload } from "lucide-react";
import useTranslation from "@babackapp/translations/use-translation";

const MAX_RESULTS = 10;

type TableResultsProps = {
  onDownload: () => void;
  data: any[];
};

export function TableResults({ data, onDownload }: TableResultsProps) {
  const t = useTranslation();
  const dataToShow =
    data.length > MAX_RESULTS ? data.slice(0, MAX_RESULTS) : data;

  const keys = Object.keys(data[0]);
  if (!keys.length) return null;

  return (
    <div className="fle flex-col gap-4">
      <div className="border rounded-xl overflow-hidden bg-white w-full">
        <Table>
          <TableHeader>
            <TableRow>
              {keys.map((key) => (
                <TableHead key={key}>{key}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {dataToShow.map((result, index) => (
              <TableRow key={index}>
                {Object.entries(result).map(([key, value]) => (
                  <ResultTableCell value={value} key={key} />
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <div className="flex justify-between items-center py-2">
        <span className="text-muted-foreground text-xs font-semibold">
          {t("ask.results.displayed", {
            displayed: dataToShow.length,
            total: data.length,
          })}
        </span>
        <Button onClick={onDownload} size="sm" variant="outline">
          {t("ask.results.downloadAll")}
          <CloudDownload className="ml-2" />
        </Button>
      </div>
    </div>
  );
}

type TableCellProps = {
  value: unknown;
};

function ResultTableCell({ value }: TableCellProps) {
  const displayValue = () => {
    if (value instanceof Date) {
      return format(value, "PP");
    } else if (
      typeof value === "object" &&
      value !== null &&
      "$date" in value
    ) {
      // Handle MongoDB date format
      // @ts-expect-error
      return format(new Date(value.$date), "PP");
    } else if (
      typeof value === "string" ||
      typeof value === "number" ||
      typeof value === "boolean"
    ) {
      return value.toString();
    } else {
      return "[Object]";
    }
  };

  return <TableCell>{displayValue()}</TableCell>;
}
